import React, { useCallback, useContext } from "react";
import { Navigate, Route } from "react-router-dom";
import Profile from "../pages/profile";
import CustomerClub from "../pages/CustomerClub";
import Report from "../pages/Report";
import BasketForm from "../pages/Market/BasketForm";
import FoodForm from "../pages/Food/FoodForm";
import SalesAndServices, {
    SalesAndServicesRedirect,
} from "../pages/SalesAndServices";
import UserCustomerClub from "../pages/UserPages/CustomerClub";
import UserProfile from "../pages/UserPages/UserProfile";
import UserDashboard from "../pages/UserPages/UserDashboard";
import UserPoll from "../pages/UserPages/UserPoll";
import UserDiscount from "../pages/UserPages/UserDiscount";
import UserOrders from "../pages/UserPages/UserOrders";
import UserExchange from "../pages/UserPages/UserExchange";
import UserBests from "../pages/UserPages/UserBests";
import Messages from "../pages/messages";
import Organizations from "../pages/Organizations";
import { GlobalStateContext } from "../layouts/GlobalStates";
import ExpSetting from "../pages/exp-settings";
import Surveys from "../pages/surveys";
import { isOrganizationMenuEnabled, isParticipationEnabled } from "../helperData/organizationSidebar";
import {
    getDefaultPanelPath,
    isAuthenticated,
} from "../helperData/panelRoutes";
import {
    APP_FLAVOR_USER,
    isStandaloneDisplay,
    readStoredAppFlavor,
} from "../helperData/appFlavor";

export function usePanelRoutes() {
    const { auth } = useContext(GlobalStateContext);
    const authed = isAuthenticated(auth);
    const canManageOrganization =
        auth.company_admin ||
        Number(auth.usertype) === 1 ||
        auth.is_superuser === true;

    const profile = useCallback(() => {
        if (!canManageOrganization) return null;
        return <Route path="profile" element={<Profile />} />;
    }, [canManageOrganization]);

    const customerClub = useCallback(() => {
        const clubEnabled = isParticipationEnabled(auth);
        const pointsEnabled = isOrganizationMenuEnabled(
            auth,
            "points",
            auth.company_admin
        );
        if (
            canManageOrganization &&
            (clubEnabled || pointsEnabled)
        ) {
            return <Route path="customer-club" element={<CustomerClub />} />;
        }
        return null;
    }, [auth, canManageOrganization]);

    const report = useCallback(() => {
        if (
            canManageOrganization &&
            isOrganizationMenuEnabled(
                auth,
                "reports",
                auth.company_admin && auth.package?.report_permission
            )
        ) {
            return <Route path="report" element={<Report />} />;
        }
        return null;
    }, [auth, canManageOrganization]);

    const salesAndServices = useCallback(() => {
        const restaurantEnabled = isOrganizationMenuEnabled(
            auth,
            "restaurant",
            auth.company_admin && auth.package?.menu_permission
        );
        const marketEnabled = isOrganizationMenuEnabled(
            auth,
            "market",
            auth.company_admin && auth.package?.market_permission
        );

        if (
            !canManageOrganization ||
            (!restaurantEnabled && !marketEnabled)
        ) {
            return null;
        }

        return (
            <>
                <Route path="sales/:tab" element={<SalesAndServices />} />
                <Route
                    path="food/:tab"
                    element={<SalesAndServicesRedirect from="food" />}
                />
                <Route
                    path="market/:tab"
                    element={<SalesAndServicesRedirect from="market" />}
                />
                <Route path="food/form/:id" element={<FoodForm />} />
                <Route path="food/form/new/:item" element={<FoodForm />} />
                <Route path="market/form/:id" element={<BasketForm />} />
                <Route path="market/form/new/:item" element={<BasketForm />} />
            </>
        );
    }, [auth, canManageOrganization]);

    const messages = useCallback(() => {
        if (
            isOrganizationMenuEnabled(
                auth,
                "messages",
                !auth.company_admin ||
                    (auth.company_admin && auth.package?.automation_permission)
            )
        ) {
            return (
                <Route path="messages/:userType" element={<Messages />} />
            );
        }
        return null;
    }, [auth]);

    const rating = useCallback(() => {
        if (
            canManageOrganization &&
            isOrganizationMenuEnabled(
                auth,
                "points",
                auth.company_admin
            )
        ) {
            return (
                <Route
                    path="rating"
                    element={
                        <Navigate
                            to="/dashboard/customer-club?tab=points"
                            replace
                        />
                    }
                />
            );
        }
        return null;
    }, [auth, canManageOrganization]);

    const reportsEnabled =
        authed &&
        canManageOrganization &&
        isOrganizationMenuEnabled(
            auth,
            "reports",
            auth.package?.report_permission
        );
    const settingsEnabled =
        authed &&
        canManageOrganization &&
        isOrganizationMenuEnabled(
            auth,
            "settings",
            auth.package?.setting_permission
        );
    const surveysEnabled =
        authed &&
        canManageOrganization &&
        isOrganizationMenuEnabled(
            auth,
            "survey",
            auth.package?.survey_permission
        );

    const installedFlavor = isStandaloneDisplay()
        ? readStoredAppFlavor()
        : undefined;
    const defaultPath = getDefaultPanelPath(auth, installedFlavor);
    const dashboardHome =
        installedFlavor === APP_FLAVOR_USER
            ? <Navigate to={defaultPath} replace />
            : reportsEnabled
            ? <Report />
            : authed
            ? <Navigate to={defaultPath} replace />
            : null;

    return (
        <>
            <Route index element={dashboardHome} />
            {profile()}
            {rating()}
            {customerClub()}
            {report()}
            {salesAndServices()}
            {settingsEnabled && (
                <Route path="exp-settings" element={<ExpSetting />} />
            )}
            {surveysEnabled && (
                <Route path="surveys" element={<Surveys />} />
            )}
            <Route path="organizations" element={<Organizations />} />
            <Route path="user/customer-club" element={<UserCustomerClub />} />
            <Route path="user/profile" element={<UserProfile />} />
            <Route path="user/dashboard" element={<UserDashboard />} />
            <Route path="user/poll" element={<UserPoll />} />
            <Route path="user/discount" element={<UserDiscount />} />
            <Route path="user/orders" element={<UserOrders />} />
            <Route path="user/exchange" element={<UserExchange />} />
            <Route path="user/bests" element={<UserBests />} />
            {messages()}
            <Route
                path="*"
                element={
                    authed ? (
                        <Navigate to={defaultPath} replace />
                    ) : null
                }
            />
        </>
    );
}
